import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { contextMismatch, entityTypeLabelKey } from '../../../generic/qr/qrEntityTypes'
import { EYEBROW_SX, codeSx } from '../../../generic/qr/qrReadingStyles'
import { verdictColor } from './qrLookupStyles'

/**
 * The label last read, left on the widget so the operator can open it again.
 *
 * The card IS the button: somebody wearing gloves gets the whole surface as a target instead of a
 * small link, and the chevron says where a tap goes. For the same reason the document counter is a
 * number and not a sentence — it answers "is there anything in there?" before the tap, and the
 * element's card answers everything else.
 *
 * The bar down the left edge carries Sun's verdict, and the warning icon next to the type names it.
 * A foreign element is flagged, never hidden: reading a drawing is not destructive, and the widget
 * stops at the flag because the dialog behind the tap spells the mismatch out in full.
 *
 * `compact` is the widget two cells tall, where the whole state gets one line: the code and the
 * counter are what survive, since the code identifies the element on its own and the counter is the
 * reason to tap.
 *
 * `lines` are what a Job detail read did, under the code (RN27): "Opened OP10 · Milling Roughing", or
 * where the work is. One line each, cut with an ellipsis, so a long machine name never pushes the
 * counter off the card; none in the compact card, where only the code fits.
 */
const QrLastReadCard = ({
  entityType = '',
  title = '',
  at = '',
  documentCount = 0,
  isForeign = false,
  interactive = true,
  compact = false,
  lines = [],
  onOpen = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const accent = verdictColor(isForeign)

  const openOnKey = (event) => {
    // A key the reader's listener swallowed belongs to a burst, not to the operator (useQrInput).
    if (event.defaultPrevented) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onOpen()
  }

  // A preview has nothing to reopen, so it is a card and not a control: no role, no focus stop.
  const interaction = interactive
    ? { role: 'button', tabIndex: 0, 'aria-label': t_('Reopen') + ' ' + title, onClick: onOpen, onKeyDown: openOnKey }
    : {}

  return (
    <Box
      {...interaction}
      data-verdict={accent}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0.75 : 1,
        width: '100%',
        height: '100%',
        // Tall enough to be worth tapping, capped so it stays a card in a widget dragged to full height;
        // a little taller when it also says what the read did.
        maxHeight: compact ? 'none' : (lines.length > 0 ? '7.5rem' : '5.5rem'),
        px: compact ? 0.75 : 1,
        py: compact ? 0.25 : 1,
        pl: compact ? 1 : 1.25,
        borderRadius: '0.75rem',
        textAlign: 'left',
        overflow: 'hidden',
        border: '1px solid ' + alpha(theme.palette[accent].main, 0.3),
        borderLeft: '3px solid ' + theme.palette[accent].main,
        backgroundColor: alpha(theme.palette[accent].main, 0.06),
        transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
        ...(interactive
          ? {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: alpha(theme.palette[accent].main, 0.13),
                borderColor: alpha(theme.palette[accent].main, 0.5),
                transform: 'translateY(-1px)'
              },
              '&:active': { transform: 'scale(0.995)' },
              '&:focus-visible': {
                outline: '2px solid ' + theme.palette[accent].main,
                outlineOffset: '2px'
              }
            }
          : {})
      })}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {!compact && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography sx={EYEBROW_SX} noWrap>{t_(entityTypeLabelKey(entityType))}</Typography>
            {isForeign && (
              <WarningAmberRoundedIcon
                color='warning'
                sx={{ fontSize: '0.95rem', flexShrink: 0 }}
                titleAccess={t_(contextMismatch(entityType).key)}
              />
            )}
          </Box>
        )}
        <Typography noWrap sx={codeSx(compact ? '0.85rem' : '1.05rem')}>{title}</Typography>
        {!compact && lines.map((line) => (
          <Typography
            key={line.text}
            variant='body2'
            noWrap
            sx={{ color: line.tone === 'warning' ? 'warning.main' : 'text.secondary', lineHeight: 1.35 }}
          >
            {line.text}
          </Typography>
        ))}
      </Box>

      {compact && isForeign && (
        <WarningAmberRoundedIcon
          color='warning'
          sx={{ fontSize: '0.95rem', flexShrink: 0 }}
          titleAccess={t_(contextMismatch(entityType).key)}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
        {/* Dimmed at zero rather than replaced by a sentence: nothing to open is a quantity too. */}
        <Box
          title={t_('Documents')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            color: documentCount > 0 ? 'text.primary' : 'text.disabled'
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: '1rem' }} />
          <Typography variant='body2' sx={{ fontWeight: 700, lineHeight: 1.2 }}>{documentCount}</Typography>
        </Box>
        {!compact && at !== '' && (
          <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.4 }}>{at}</Typography>
        )}
      </Box>

      {interactive && (
        <ChevronRightRoundedIcon
          sx={{ fontSize: '1.4rem', color: accent + '.main', opacity: 0.85, flexShrink: 0 }}
        />
      )}
    </Box>
  )
}

QrLastReadCard.propTypes = {
  /** In either form; the label comes from the shared catalogue. */
  entityType: PropTypes.string,
  /** The element's code, as Sun titled it. */
  title: PropTypes.string,
  /** Local time of the read, '' when it was not recorded. */
  at: PropTypes.string,
  /** Documents anywhere in what was resolved, structure included. */
  documentCount: PropTypes.number,
  isForeign: PropTypes.bool,
  interactive: PropTypes.bool,
  /** One line only: the widget is two cells tall. */
  compact: PropTypes.bool,
  /** What the read did, already translated; 'warning' paints the line in the warning colour. */
  lines: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, tone: PropTypes.oneOf(['default', 'warning']) })),
  onOpen: PropTypes.func
}

export default QrLastReadCard
